<!--
  TinoKPICards.vue — 4-column KPI metrics strip.
  Shows Prospects, Quotes, Revenue, Autonomy Rate with trend deltas.
-->
<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  period: { type: String, default: 'week' },
  orders: { type: Array, default: () => [] },
  conversations: { type: Array, default: () => [] },
})

// Static mock data by period — replace with real computed values once API returns historical data
const kpiData = {
  today:  { prospects: 14,  quotes: 5,  revenue: '$492',     autonomy: 94 },
  week:   { prospects: 78,  quotes: 28, revenue: '$4,200',   autonomy: 91 },
  month:  { prospects: 312, quotes: 94, revenue: '$18,700',  autonomy: 89 },
}

const deltas = {
  today: { prospects: '+4.2%',  quotes: '+1.5%',  revenue: '-2.5%',  autonomy: '+3.2%' },
  week:  { prospects: '+12.1%', quotes: '+8.7%',  revenue: '+15.4%', autonomy: '+2.1%' },
  month: { prospects: '+21.3%', quotes: '+18.2%', revenue: '+24.6%', autonomy: '+1.8%' },
}

const kpi = computed(() => kpiData[props.period] || kpiData.week)
const delta = computed(() => deltas[props.period] || deltas.week)

const cards = computed(() => [
  {
    key: 'prospects',
    icon: 'mdi-account-group-outline',
    iconBg: '#eef2ff',
    iconColor: '#6366f1',
    label: t('dashboard.tino.kpi.prospects'),
    value: kpi.value.prospects.toLocaleString(),
    delta: delta.value.prospects,
    deltaUp: !delta.value.prospects.startsWith('-'),
    sub: `${delta.value.prospects} vs previous period`,
  },
  {
    key: 'quotes',
    icon: 'mdi-file-document-outline',
    iconBg: '#fff7ed',
    iconColor: '#f59e0b',
    label: t('dashboard.tino.kpi.quotes'),
    value: kpi.value.quotes,
    delta: delta.value.quotes,
    deltaUp: true,
    sub: t('dashboard.tino.kpi.quotesAuto'),
  },
  {
    key: 'revenue',
    icon: 'mdi-credit-card-outline',
    iconBg: '#f0fdf4',
    iconColor: '#22c55e',
    label: t('dashboard.tino.kpi.revenue'),
    value: kpi.value.revenue,
    delta: delta.value.revenue,
    deltaUp: !delta.value.revenue.startsWith('-'),
    sub: t('dashboard.tino.kpi.revenueConfirmed'),
  },
  {
    key: 'autonomy',
    icon: 'mdi-lightning-bolt',
    iconBg: '#f3eeff',
    iconColor: '#8b5cf6',
    label: t('dashboard.tino.kpi.autonomy'),
    value: `${kpi.value.autonomy}%`,
    delta: delta.value.autonomy,
    deltaUp: true,
    sub: t('dashboard.tino.kpi.conversationsAuto'),
  },
])
</script>

<template>
  <div class="tino-kpi-grid">
    <VCard
      v-for="card in cards"
      :key="card.key"
      class="tino-kpi-card"
      flat
    >
      <VCardText class="pa-5">
        <div class="d-flex align-center justify-space-between mb-4">
          <div
            class="tino-kpi-icon"
            :style="{ background: card.iconBg }"
          >
            <VIcon :icon="card.icon" size="20" :color="card.iconColor" />
          </div>
          <span
            class="text-xs font-weight-semibold d-flex align-center gap-1"
            :class="card.deltaUp ? 'text-success' : 'text-error'"
          >
            <VIcon
              :icon="card.deltaUp ? 'mdi-trending-up' : 'mdi-trending-down'"
              size="14"
            />
            {{ card.delta }}
          </span>
        </div>

        <div>
          <p class="text-h5 font-weight-bold text-high-emphasis mb-0">{{ card.value }}</p>
          <p class="text-xs font-weight-medium text-medium-emphasis mt-1">{{ card.label }}</p>
        </div>

        <p class="text-xs text-disabled mt-3 mb-0">{{ card.sub }}</p>
      </VCardText>
    </VCard>
  </div>
</template>

<style scoped lang="scss">
.tino-kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
}

.tino-kpi-card {
  border: 1px solid rgba(var(--v-border-color), 0.1);
  border-radius: 16px !important;
  background: rgb(var(--v-theme-surface));
}

.tino-kpi-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
</style>
